import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowRight } from "lucide-react";
import Btn from "../components/Btn";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentResult() {
  const [sp] = useSearchParams();
  const orderId = sp.get("order_id");
  const cancelled = sp.get("cancel") === "1";
  const [state, setState] = useState(cancelled ? "cancelled" : "checking"); // checking | paid | pending | cancelled | error
  const { refresh } = useAuth();
  const nav = useNavigate();
  const tries = useRef(0);

  useEffect(() => {
    if (cancelled || !orderId) { if (!orderId) setState("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/doku/status/${orderId}`);
        if (stop) return;
        if (data.granted || data.payment_status === "paid") {
          setState("paid");
          await refresh();
          return;
        }
      } catch (e) { /* keep polling a few times */ }
      tries.current += 1;
      if (tries.current >= 6) { setState("pending"); return; }
      if (!stop) setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [orderId, cancelled, refresh]);

  const cfg = {
    checking: { icon: Loader2, spin: true, color: "text-su-primary", title: "Memeriksa pembayaran…", desc: "Mohon tunggu sebentar, kami sedang mengonfirmasi pembayaranmu ke iPaymu." },
    paid: { icon: CheckCircle2, color: "text-su-success", title: "Pembayaran Berhasil! 🎉", desc: "Manfaatmu sudah aktif. Selamat belajar, pejuang!" },
    pending: { icon: Clock, color: "text-su-energy", title: "Pembayaran Diproses ⏳", desc: "Pembayaranmu sedang diproses. Manfaat akan aktif otomatis setelah pembayaran dikonfirmasi. Cek Riwayat Transaksi secara berkala." },
    cancelled: { icon: XCircle, color: "text-su-danger", title: "Pembayaran Dibatalkan", desc: "Kamu membatalkan pembayaran. Kamu bisa mencoba lagi kapan saja." },
    error: { icon: XCircle, color: "text-su-danger", title: "Order Tidak Ditemukan", desc: "Kami tidak menemukan data order ini." },
  }[state];
  const Icon = cfg.icon;

  return (
    <div className="min-h-screen bg-su-bg text-su-text grid place-items-center px-4" data-testid="payment-result">
      <div className="card-chunky p-8 max-w-md w-full text-center">
        <div className={`w-20 h-20 rounded-3xl grid place-items-center mx-auto mb-4 bg-su-surface border-2 border-su-border ${cfg.color}`}>
          <Icon className={`w-10 h-10 ${cfg.spin ? "animate-spin" : ""}`} />
        </div>
        <h1 className="font-heading font-black text-2xl mb-2" data-testid="payment-result-title">{cfg.title}</h1>
        <p className="text-su-muted font-medium mb-6">{cfg.desc}</p>
        <div className="flex flex-col gap-2">
          {state === "paid" && <Btn className="w-full" data-testid="go-dashboard" onClick={() => nav("/dashboard")}>Ke Dashboard <ArrowRight className="w-4 h-4" /></Btn>}
          {(state === "pending" || state === "cancelled" || state === "error") && (
            <>
              <Btn className="w-full" data-testid="go-pricing" onClick={() => nav("/pricing")}>Kembali ke Paket</Btn>
              <Link to="/transactions" data-testid="go-transactions" className="text-sm font-heading font-bold text-su-primary hover:underline">Lihat Riwayat Transaksi</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
